/**
 * Photo Attachments — which of a patient's photos belong on a given plan.
 *
 * Photos are attached to the PATIENT, so every saved plan for that patient
 * draws from one library. This module holds the rule that decides what a
 * particular plan actually shows.
 *
 * The rule (product decision, 2026-08-11):
 *
 *   - a photo with NO teeth tagged is a "general" photo and ALWAYS shows
 *   - a tagged photo shows iff at least one of its teeth is on this plan
 *
 * The untagged case is the load-bearing half. Tagging is optional, so the
 * alternative — hide anything untagged — means a TC who uploads a photo and
 * doesn't tag it gets silence: no photo for the patient, no warning for them.
 * A photo that appears when it shouldn't is a conversation; a photo that
 * silently never appears is a bug nobody reports.
 *
 * All of this runs client-side. Every surface (Builder, Present Mode, patient
 * view) already holds the plan's procedures, so the server just returns the
 * whole library and never needs a per-tooth query. See the migration header in
 * tx-presenter-service/migrations/009-create-patient-photos-table.sql.
 */

/**
 * Coerce one Open Dental ToothNum to an int 1-32, or null.
 *
 * OD stores ToothNum as a raw varchar(2): "3", "14", blank for procedures that
 * aren't tooth-specific (exams, cleanings), and letters A-T for primary teeth.
 * Photo tagging is permanent dentition only, so a letter resolves to null here
 * and simply never matches — the same way groupProcedures.js buckets it as
 * "Other". Don't "fix" that without also extending the tooth selector.
 */
export function normalizeTooth(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseInt(String(raw).replace('#', '').trim(), 10);
  if (!Number.isFinite(n) || n < 1 || n > 32) return null;
  return n;
}

/**
 * The set of teeth a plan actually covers, as a Set of ints.
 *
 * @param {Array} procedures  plan procedures, each with a `toothNum`
 * @returns {Set<number>}
 */
export function planToothNums(procedures) {
  const out = new Set();
  (procedures || []).forEach((p) => {
    const n = normalizeTooth(p && p.toothNum);
    if (n !== null) out.add(n);
  });
  return out;
}

/**
 * Does this one photo belong on a plan covering `planTeeth`?
 */
export function photoAppliesToPlan(photo, planTeeth) {
  const teeth = (photo && photo.teeth) || [];
  if (!Array.isArray(teeth) || teeth.length === 0) return true; // general photo
  const set = planTeeth instanceof Set ? planTeeth : new Set(planTeeth || []);
  return teeth.some((t) => {
    const n = normalizeTooth(t);
    return n !== null && set.has(n);
  });
}

/**
 * The subset of a patient's library that this plan shows, order preserved.
 */
export function filterPhotosForPlan(photos, planTeeth) {
  const set = planTeeth instanceof Set ? planTeeth : new Set(planTeeth || []);
  return (photos || []).filter((p) => photoAppliesToPlan(p, set));
}

/**
 * Lookup for the camera icon in the Itemized / By Region views:
 * `{ [toothNum]: true }` for every tooth that has at least one photo.
 *
 * Intersected with the plan's own teeth on purpose. A photo tagged #2 and #31
 * on a plan that only treats #2 is visible (it matched), but #31 must not
 * sprout an icon — that tooth isn't on this plan, and an icon there would
 * promise a row that doesn't exist.
 *
 * @param {Array}   visiblePhotos  already through filterPhotosForPlan
 * @param {Set|Array} planTeeth
 * @returns {Object} plain object so Vue 2 reactivity and template lookups work
 */
export function photoTeethMap(visiblePhotos, planTeeth) {
  const set = planTeeth instanceof Set ? planTeeth : new Set(planTeeth || []);
  const map = {};
  (visiblePhotos || []).forEach((photo) => {
    ((photo && photo.teeth) || []).forEach((t) => {
      const n = normalizeTooth(t);
      if (n !== null && set.has(n)) map[n] = true;
    });
  });
  return map;
}

/**
 * The photos tagged with one specific tooth — what the lightbox opens to when
 * a camera icon is clicked.
 */
export function photosForTooth(visiblePhotos, toothNum) {
  const target = normalizeTooth(toothNum);
  if (target === null) return [];
  return (visiblePhotos || []).filter((photo) =>
    ((photo && photo.teeth) || []).some((t) => normalizeTooth(t) === target)
  );
}
